import { useContext, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import useIsMobile from '../../../../utils/detectDevice'
import { HStack } from '../../../../utils/Stack'
import {
  AddUserCurrencyMutation,
  AddUserCurrencyMutationVariables,
  GetUserCurrencyQuery,
  useAddUserCurrencyMutation,
  useGetAllTransactionsQuery,
  useGetCompanyWiseDataQuery,
  useGetUserCurrencyQuery,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { GlobalContext, GlobalContextType } from '../../../../app/pages/store/context/globalContext'
import Toast, { ToastType } from '../../../../utils/toast'
import { SelectOptionType } from '../../../../utils/MultiSelect'
import { Actions } from '../../../../app/pages/store/context/reducer'
import { currencies } from '../../../../utils/CurrencyList'

const currencyOptions: SelectOptionType[] = currencies.map((currency) => ({
  value: currency,
  label: currency,
}))

export function CurrencyDropdown() {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { state, dispatch } = useContext<GlobalContextType>(GlobalContext)
  const { userCurrency } = state
  const [currency, setCurrency] = useState<SelectOptionType>()
  const allTransactionsQueryKey = useGetAllTransactionsQuery.getKey()
  const companyPageQueryKey = useGetCompanyWiseDataQuery.getKey()

  const { isMobile } = useIsMobile(1024)

  const selectedCurrency = useGetUserCurrencyQuery<GetUserCurrencyQuery, Error>(
    graphqlRequestClient,
    {},
    { enabled: userCurrency === '' }
  )

  const mutateAddUserCurrency = useAddUserCurrencyMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (
        data: AddUserCurrencyMutation,
        _variables: AddUserCurrencyMutationVariables,
        _context: unknown
      ) => {
        Toast('Updated User Currency Successfully', ToastType.success)
        queryClient.invalidateQueries(allTransactionsQueryKey)
        queryClient.invalidateQueries(companyPageQueryKey) // not working TODO: Fix this
        window.location.reload()
      },
      onError: (error: any) => {
        Toast(error.response.errors[0].message, ToastType.error)
      },
    },
    {}
  )

  if (selectedCurrency.isSuccess && !currency) {
    setCurrency({
      value: selectedCurrency.data?.getUserCurrency || 'INR - ₹',
      label: selectedCurrency.data?.getUserCurrency || 'INR - ₹',
    })
    dispatch({
      type: Actions.setUserCurrency,
      payload: selectedCurrency.data?.getUserCurrency || 'INR - ₹',
    })
  }
  useEffect(() => {
    if (userCurrency === undefined) {
      setCurrency({ value: 'INR - ₹', label: 'INR - ₹' })
    }

    if (userCurrency !== '') {
      const selectedObject = currencyOptions.find((element) => element.value === userCurrency)
      if (selectedObject) {
        setCurrency(selectedObject)
      }
    }
  }, [userCurrency])

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setIsOpen])

  function handleCurrencySelect(option: SelectOptionType) {
    setIsOpen(false)
    if (option.value === currency?.value) return

    setCurrency(option)
    dispatch({ type: Actions.setUserCurrency, payload: option.label! })
    if (option.value) {
      mutateAddUserCurrency.mutate({
        input: {
          userCurrency: option.label,
        },
      })
    }
  }

  return (
    <div className='position-relative' ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='border-0 border-top-bar-bottom h-55px w-100px px-3 text-gray-700 text-hover-primary'
        type='button'
        aria-label='Select currency'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        <HStack className='d-flex justify-content-between align-items-center gap-2'>
          <span className='fw-bold fs-6 text-truncate' style={{ maxWidth: 58 }}>
            {currency ? currency.label : 'Select'}
          </span>

          <svg
            className={`w-15px h-15px flex-shrink-0 transition-transform ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7' />
          </svg>
        </HStack>
      </button>

      {isOpen && (
        <div
          className={`position-absolute start-50 translate-middle-x bg-white border border-gray-200 rounded shadow-lg z-10 p-2 ${
            isMobile ? 'mt-8' : 'mt-2'
          }`}
          style={{ top: '100%', width: 112 }}
        >
          <div
            className='mh-300px overflow-auto scroll-smooth'
            style={{ overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}
            role='listbox'
            aria-label='Currency options'
          >
            {currencyOptions.map((option, index) => {
              const isSelected = option.value === currency?.value

              return (
                <button
                  key={String(option.value)}
                  type='button'
                  role='option'
                  aria-selected={isSelected}
                  className={`position-relative w-100 border-0 rounded py-2 px-2 d-flex align-items-center justify-content-center ${
                    isSelected
                      ? 'bg-light-primary text-primary fw-bold'
                      : 'bg-transparent bg-hover-gray-light text-gray-700'
                  } ${index !== 0 ? 'mt-1' : ''}`}
                  onClick={() => handleCurrencySelect(option)}
                >
                  <span className='fs-7 text-nowrap'>{option.value}</span>
                  {isSelected && (
                    <Icon
                      icon='mdi:check'
                      className='position-absolute end-0 me-1 text-primary'
                      width={16}
                      height={16}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
